import { Table } from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PaginationProps<TData> {
  table: Table<TData>;
}

export const Pagination = <TData,>({ table }: PaginationProps<TData>) => (
  <div className='flex items-center justify-between px-2'>
    <div className='text-muted-foreground flex-1 text-sm'>
      Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
    </div>
    <div className='flex items-center space-x-6 lg:space-x-8'>
      <div className='flex items-center space-x-2'>
        <p className='text-sm font-medium'>Rows per page</p>
        <Select
          value={`${table.getState().pagination.pageSize}`}
          onValueChange={(value) => {
            table.setPageSize(Number(value));
          }}>
          <SelectTrigger className='h-8 w-[70px]'>
            <SelectValue placeholder={table.getState().pagination.pageSize} />
          </SelectTrigger>
          <SelectContent side='top'>
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <SelectItem
                key={pageSize}
                value={`${pageSize}`}>
                {pageSize}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className='flex items-center space-x-2'>
        <Button
          variant='outline'
          className='hidden h-8 w-8 p-0 lg:flex'
          onClick={() => {
            table.setPageIndex(0);
          }}
          disabled={!table.getCanPreviousPage()}>
          <span className='sr-only'>First page</span>
          <ChevronsLeft />
        </Button>
        <Button
          variant='outline'
          className='h-8 w-8 p-0'
          onClick={() => {
            table.previousPage();
          }}
          disabled={!table.getCanPreviousPage()}>
          <span className='sr-only'>Previous page</span>
          <ChevronLeft />
        </Button>
        <Button
          variant='outline'
          className='h-8 w-8 p-0'
          onClick={() => {
            table.nextPage();
          }}
          disabled={!table.getCanNextPage()}>
          <span className='sr-only'>Next page</span>
          <ChevronRight />
        </Button>
        <Button
          variant='outline'
          className='hidden h-8 w-8 p-0 lg:flex'
          onClick={() => {
            table.setPageIndex(table.getPageCount() - 1);
          }}
          disabled={!table.getCanNextPage()}>
          <span className='sr-only'>Last page</span>
          <ChevronsRight />
        </Button>
      </div>
    </div>
  </div>
);
